import * as React from "react";
import DatePicker, {registerLocale} from "react-datepicker";
import enGB from "date-fns/locale/en-GB";
import "react-datepicker/src/stylesheets/datepicker.scss";
import * as moment from "moment";

interface ICustomDatePickerState {
    date: Date | null;
    showPreviousMonths: boolean;
}

interface IProps {
    id?: string;
    errorText?: string;
    showErrorMessage?: boolean;
    onDateSelect: (state: string) => void;
    date: string;
    minDate: string;
    mtcMaxDate?: string | null;
}

export class ClaimDatePicker extends React.Component<IProps, ICustomDatePickerState> {
    private maxDate: Date;
    private calendarRef: any;

    constructor(props: IProps) {
        super(props);

        this.calendarRef = React.createRef();
        this.maxDate = this.props.mtcMaxDate ? moment(moment.utc(this.props.mtcMaxDate, moment.ISO_8601).format("YYYY-MM-DD")).toDate() : moment().toDate();

        this.state = {
            date: this.props.date ? moment(this.props.date).toDate() : null,
            showPreviousMonths: true
        };

        registerLocale("enGB", enGB);
    }

    public componentDidUpdate() {
        if (this.state.date) {
            const dateInNextMonth: boolean = moment(this.state.date!).isAfter(new Date(), "month");
            const maxDateInMonthAfter: boolean = moment(this.state.date!).isBefore(this.maxDate, "month");
            const showPreviousMonths: boolean = dateInNextMonth && !maxDateInMonthAfter;

            if (this.state.showPreviousMonths !== showPreviousMonths) {
                this.setState({showPreviousMonths})
            }
        } else if (this.state.showPreviousMonths === false) {
            this.setState({showPreviousMonths: true});
        }
    }

    public render() {
        let calendarIcon: string;

        if (this.props.showErrorMessage) {
            calendarIcon = "calendar-icon-invalid";
        } else if (this.state.date) {
            calendarIcon = "calendar-icon-selected";
        } else {
            calendarIcon = "calendar-icon";
        }

        const DateInput = ({value, onClick}: any): React.ReactNode => {
            return (
                <div className="claimDatePicker row">
                    <div onClick={onClick} onKeyPress={onClick} className="col-md-6">
                        <input type="radio" name="date" value="calendar" id="datePickerCalendar" role="dialog"
                            checked={!!this.state.date}/>
                        <label className={this.props.showErrorMessage ? "is-invalid" : ""}
                            htmlFor="datePickerCalendar"
                            tabIndex={0}
                            aria-label="Choose date from calendar"
                        >
                            {this.state.date ? value : "Please select date"}
                            <span className={calendarIcon}/>
                            <span className={"calendar-arrow-icon"}/>
                        </label>
                    </div>
                </div>
            );
        };

        return (
            <div className="form-group" id={this.props.id ?? ""}>
                <label htmlFor="accidentDate">When did the damage happen?</label>
                <div className="w-100">
                    <DatePicker
                        ref={this.calendarRef}
                        onChange={this.handleChange}
                        selected={this.state.date}
                        customInput={<DateInput/>}
                        locale="enGB"
                        dateFormat="dd MMM yyyy"
                        minDate={moment(this.props.minDate).toDate()}
                        maxDate={this.maxDate}
                        showDisabledMonthNavigation={false}
                        disabledKeyboardNavigation={true}
                        shouldCloseOnSelect={false}
                        monthsShown={this.monthsToShow()}
                        showPopperArrow={false}
                        withPortal={true}
                        showPreviousMonths={this.state.showPreviousMonths}>
                        <div className={"d-flex justify-content-center mx-5"}>
                            {this.renderSelectDateButton()}
                        </div>
                    </DatePicker>
                </div>
                {this.props.showErrorMessage &&
                    <div className="mb-3 invalid-feedback d-block">{this.props.errorText}</div>
                }
            </div>
        );
    }

    private setDate = (): void => {
        if (this.state.date) {
            this.calendarRef.current.setOpen(false);
        }
    };

    private monthsToShow = (): number => {
        const maxDateMoment: moment.Moment = moment(this.maxDate);
        const minDateMoment: moment.Moment = moment(this.props.minDate);

        return this.getNumberOfMonths(maxDateMoment) - this.getNumberOfMonths(minDateMoment) + 1;
    };

    private renderSelectDateButton(): React.ReactNode {
        return (
            <button type="button" className="btn btn-primary btn-next-arrow-separate" onClick={this.setDate} id={"set-date"} disabled={!this.state.date}>Select date
                <img className="arrow-next" alt="Arrow icon" src="/img/arrow-next.png"/>
            </button>
        )
    }

    private handleChange = (date: Date): void => {
        this.props.onDateSelect(moment(date).format("YYYY-MM-DD"));
        this.setState({date});
    };

    private getNumberOfMonths(date: moment.Moment): number {
        return date.month() + date.year() * 12;
    }
}
